import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Badge } from '@shared/components/ui/badge';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import { Label } from '@shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import {
  QrCode,
  Download,
  Printer,
  Calendar,
  Mail,
  User,
  Building2,
  Copy,
  CheckCircle2,
  Plus,
  RefreshCw,
  Users,
  AlertCircle,
  Upload,
  FolderKanban,
  MapPin,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '@shared/supabaseClient';
import { useAuth } from '@shared/contexts/AuthContext';
import { useToast } from '@shared/components/ui/use-toast';

/**
 * QR Code Generator
 * Display QR codes for active passes (pass_token) to use at kiosk check-in
 * Now with Bulk QR Generation support
 */
const QRCodeGenerator = () => {
  const { user } = useAuth();
  const [passes, setPasses] = useState([]);
  const [filteredPasses, setFilteredPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewFilter, setViewFilter] = useState('active'); // 'all', 'active', 'revoked', 'expired', 'last6'
  const [activeView, setActiveView] = useState('individual'); // 'individual' | 'bulk' | 'import' | 'projects'
  const [selectedProject, setSelectedProject] = useState(null); // { id, name, passes[] }
  
  // Bulk creation state
  const [selectedPasses, setSelectedPasses] = useState([]);
  const [bulkConfirmation, setBulkConfirmation] = useState(false);
  const [processingBulk, setProcessingBulk] = useState(false);
  
  // Bulk import state
  const [csvFile, setCsvFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importConfirmation, setImportConfirmation] = useState(false);
  const [processingImport, setProcessingImport] = useState(false);
  
  const { toast } = useToast();

  // Form state for generating new QR code
  const [formData, setFormData] = useState({
    person_name: '',
    person_email: '',
    person_company: '',
    person_type: 'visitor',
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: '',
  });

  useEffect(() => {
    if (user) {
      loadPasses();
    }
  }, [user]);

  useEffect(() => {
    // Apply filters whenever passes, searchTerm, or viewFilter changes
    let filtered = [...passes];

    // Apply view filter
    if (viewFilter === 'last6') {
      filtered = filtered.slice(0, 6);
    } else if (viewFilter === 'active') {
      filtered = filtered.filter(p => p.status === 'active');
    } else if (viewFilter === 'revoked') {
      filtered = filtered.filter(p => p.status === 'revoked');
    } else if (viewFilter === 'expired') {
      filtered = filtered.filter(p => p.status === 'expired');
    }
    // 'all' — no additional filtering

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(pass =>
        pass.person_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pass.person_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pass.person_company?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredPasses(filtered);
  }, [passes, searchTerm, viewFilter]);

  const loadPasses = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('passes')
        .select('*, depots(name), projects:project_id(project_name)')
        .order('created_at', { ascending: false });

      // Scope to depot for depot-specific staff only.
      // Admins, owners and system-admins see all passes even if they have a depot_id set.
      const isAdmin = ['admin', 'owner', 'system-admin'].includes(user?.role);
      if (user?.depot_id && !isAdmin) {
        query = query.eq('depot_id', user.depot_id);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      setPasses(data || []);
    } catch (error) {
      console.error('Error loading passes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load passes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    toast({
      title: 'Use approvals to generate passes',
      description: 'Create/approve an induction to generate a pass, then print the QR code here.',
    });
    setIsGenerateModalOpen(false);
  };

  const handlePrintQR = (token) => {
    setSelectedToken(token);
    setIsPrintModalOpen(true);
    
    // Wait for modal to render, then print
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const handleDownloadQR = (token) => {
    const canvas = document.getElementById(`qr-${token.id}`);
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `qr-code-${token.person_name.replace(/\s+/g, '-')}.png`;
      link.href = url;
      link.click();
      
      toast({
        title: 'Download Complete',
        description: 'QR code image has been downloaded',
      });
    }
  };

  const handleCopyLink = (token) => {
    const value = token.pass_token || token.pass_code;
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast({
      title: 'Link Copied',
      description: 'Pass token copied to clipboard',
    });
  };

  const handleEmailQR = async (token) => {
    // In real app, this would send an email
    toast({
      title: 'Email Sent',
      description: `QR code sent to ${token.person_email}`,
    });
  };

  const getStatusBadge = (token) => {
    const now = new Date();
    const validFrom = new Date(token.valid_from);
    const validUntil = new Date(token.valid_until);

    if (token.revoked_at) {
      return { text: 'Revoked', variant: 'bg-gray-500 text-white' };
    } else if (now < validFrom) {
      return { text: 'Pending', variant: 'bg-yellow-500 text-white' };
    } else if (now > validUntil) {
      return { text: 'Expired', variant: 'bg-red-500 text-white' };
    } else {
      return { text: 'Active', variant: 'bg-green-500 text-white' };
    }
  };

  const getPersonTypeBadge = (type) => {
    const variants = {
      employee: 'bg-green-500',
      contractor: 'bg-blue-500',
      visitor: 'bg-purple-500',
    };
    return variants[type] || 'bg-gray-500';
  };

  // Bulk QR generation handler
  const handleBulkGenerate = async () => {
    if (!bulkConfirmation) {
      toast({
        title: 'Confirmation Required',
        description: 'Please confirm you understand bulk generation creates passes for all selected items',
        variant: 'destructive',
      });
      return;
    }

    if (selectedPasses.length === 0) {
      toast({
        title: 'No Passes Selected',
        description: 'Please select at least one pass to generate',
        variant: 'destructive',
      });
      return;
    }

    setProcessingBulk(true);
    try {
      // Log audit event for bulk generation
      await supabase.from('audit_log').insert({
        depot_id: user.depot_id,
        user_id: user.id,
        action: 'bulk_qr_generation',
        entity_type: 'passes',
        entity_id: user.depot_id,
        changes: {
          pass_count: selectedPasses.length,
          pass_ids: selectedPasses,
          generated_by: user.email,
        },
      });

      toast({
        title: 'Success',
        description: `Generated QR codes for ${selectedPasses.length} passes`,
      });

      // Reset selections
      setSelectedPasses([]);
      setBulkConfirmation(false);
      
      // Reload passes
      await loadPasses();
    } catch (error) {
      console.error('Error in bulk generation:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete bulk generation',
        variant: 'destructive',
      });
    } finally {
      setProcessingBulk(false);
    }
  };

  const togglePassSelection = (passId) => {
    setSelectedPasses(prev =>
      prev.includes(passId)
        ? prev.filter(id => id !== passId)
        : [...prev, passId]
    );
  };

  // CSV Template Download
  const handleDownloadTemplate = () => {
    const csvContent = `Name,Email,Company,Person Type,Valid From,Valid Until
John Smith,john.smith@example.com,ABC Construction,contractor,2026-02-03,2026-12-31
Jane Doe,jane.doe@example.com,XYZ Engineering,contractor,2026-02-03,2026-12-31`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk_pass_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Template Downloaded',
      description: 'Fill in the CSV template and upload it to create passes in bulk',
    });
  };

  // CSV File Upload Handler
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast({
        title: 'Invalid File',
        description: 'Please upload a CSV file',
        variant: 'destructive',
      });
      return;
    }

    setCsvFile(file);
    parseCSV(file);
  };

  // Parse CSV File
  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast({
          title: 'Empty File',
          description: 'CSV file must contain headers and at least one data row',
          variant: 'destructive',
        });
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const data = [];
      const errors = [];

      // Validate headers
      const requiredHeaders = ['name', 'email', 'person type', 'valid from', 'valid until'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
      
      if (missingHeaders.length > 0) {
        setImportErrors([`Missing required columns: ${missingHeaders.join(', ')}`]);
        return;
      }

      // Parse data rows
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = {};
        
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });

        // Validate row
        const rowErrors = [];
        if (!row['name']) rowErrors.push(`Row ${i}: Name is required`);
        if (!row['email']) rowErrors.push(`Row ${i}: Email is required`);
        if (!row['email']?.includes('@')) rowErrors.push(`Row ${i}: Invalid email format`);
        if (!row['person type']) rowErrors.push(`Row ${i}: Person Type is required`);
        if (!['contractor', 'visitor', 'employee'].includes(row['person type']?.toLowerCase())) {
          rowErrors.push(`Row ${i}: Person Type must be contractor, visitor, or employee`);
        }
        if (!row['valid from']) rowErrors.push(`Row ${i}: Valid From is required`);
        if (!row['valid until']) rowErrors.push(`Row ${i}: Valid Until is required`);

        if (rowErrors.length > 0) {
          errors.push(...rowErrors);
        } else {
          data.push({
            person_name: row['name'],
            person_email: row['email'],
            person_company: row['company'] || '',
            person_type: row['person type'].toLowerCase(),
            valid_from: row['valid from'],
            valid_until: row['valid until'],
          });
        }
      }

      setParsedData(data);
      setImportErrors(errors);

      if (errors.length === 0 && data.length > 0) {
        toast({
          title: 'CSV Parsed Successfully',
          description: `${data.length} valid records found. Review and confirm to import.`,
        });
      } else if (errors.length > 0) {
        toast({
          title: 'Validation Errors',
          description: `Found ${errors.length} error(s). Please fix and re-upload.`,
          variant: 'destructive',
        });
      }
    };

    reader.readAsText(file);
  };

  // Bulk Import Handler
  const handleBulkImport = async () => {
    if (!importConfirmation) {
      toast({
        title: 'Confirmation Required',
        description: 'Please confirm you understand this will create passes for all imported records',
        variant: 'destructive',
      });
      return;
    }

    if (parsedData.length === 0) {
      toast({
        title: 'No Data',
        description: 'Please upload a CSV file with valid data',
        variant: 'destructive',
      });
      return;
    }

    setProcessingImport(true);
    try {
      // Add depot_id to all records
      const passesWithDepot = parsedData.map(pass => ({
        ...pass,
        depot_id: user.depot_id,
      }));

      // Insert all passes in bulk
      const { data, error } = await supabase
        .from('passes')
        .insert(passesWithDepot)
        .select();

      if (error) throw error;

      // Log audit event
      await supabase.from('audit_log').insert({
        depot_id: user.depot_id,
        user_id: user.id,
        action: 'bulk_pass_import',
        entity_type: 'passes',
        entity_id: user.depot_id,
        changes: {
          pass_count: parsedData.length,
          imported_by: user.email,
          filename: csvFile.name,
        },
      });

      toast({
        title: 'Success',
        description: `Successfully created ${data.length} passes with QR codes`,
      });

      // Reset state
      setCsvFile(null);
      setParsedData([]);
      setImportErrors([]);
      setImportConfirmation(false);
      
      // Reload passes
      await loadPasses();
      
      // Switch to Individual QR Codes tab to show new passes
      setActiveView('individual');
    } catch (error) {
      console.error('Error in bulk import:', error);
      toast({
        title: 'Error',
        description: 'Failed to import passes. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setProcessingImport(false);
    }
  };

  const stats = {
    active: passes.filter(t => {
      const now = new Date();
      return !t.revoked_at && new Date(t.valid_from) <= now && new Date(t.valid_until) >= now;
    }).length,
    pending: passes.filter(t => !t.revoked_at && new Date(t.valid_from) > new Date()).length,
    revoked: passes.filter(t => t.revoked_at).length,
    expired: passes.filter(t => {
      const now = new Date();
      return !t.revoked_at && new Date(t.valid_until) < now;
    }).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading QR codes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">QR Code Management</h1>
          <p className="text-muted-foreground mt-1">
            View and print QR codes for approved passes, or generate in bulk
          </p>
        </div>
        <Button onClick={handleGenerateQR} className="flex items-center gap-2" variant="outline">
          <Plus className="h-4 w-4" />
          Generate Pass (Use Approvals)
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-green-500">{stats.active}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
              </div>
              <Calendar className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Revoked</p>
                <p className="text-2xl font-bold text-blue-500">{stats.revoked}</p>
              </div>
              <QrCode className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expired</p>
                <p className="text-2xl font-bold text-red-500">{stats.expired}</p>
              </div>
              <RefreshCw className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Individual vs Bulk vs Import */}
      <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
        <TabsList className="grid w-full max-w-3xl grid-cols-4">
          <TabsTrigger value="individual" className="flex items-center gap-2">
            <QrCode className="h-4 w-4" />
            Individual QR Codes
          </TabsTrigger>
          <TabsTrigger value="projects" className="flex items-center gap-2" onClick={() => setSelectedProject(null)}>
            <FolderKanban className="h-4 w-4" />
            Projects
          </TabsTrigger>
          <TabsTrigger value="bulk" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Bulk Creation
          </TabsTrigger>
          <TabsTrigger value="import" className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            Bulk Import
          </TabsTrigger>
        </TabsList>

        {/* Individual Tab */}
        <TabsContent value="individual" className="space-y-4 mt-6">
          {/* Search and Filter */}
          <div className="flex gap-4 w-full items-center">
            <Select value={viewFilter} onValueChange={setViewFilter}>
              <SelectTrigger className="w-[200px] text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Passes</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="last6">Last 6 Generated</SelectItem>
              </SelectContent>
            </Select>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 h-10 px-3 text-sm text-foreground border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
            />
          </div>

      {/* QR Code List */}
      {filteredPasses.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <QrCode className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">
                {searchTerm ? 'No passes match your search' : 'No passes available'}
              </h3>
              <p className="text-muted-foreground mt-2">
                {searchTerm 
                  ? 'Try a different search term'
                  : 'Approve an induction to generate a pass'
                }
              </p>
              {!searchTerm && (
                <Button onClick={handleGenerateQR} className="mt-4" variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Pass (Use Approvals)
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPasses.map((token) => {
            const status = getStatusBadge(token);
            const typeBadge = getPersonTypeBadge(token.person_type);
            const passValue = token.pass_token || token.pass_code;
            
            return (
              <Card key={token.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{token.person_name}</CardTitle>
                    <Badge className={status.variant}>{status.text}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* QR Code */}
                  <div className="flex justify-center bg-white p-4 rounded-lg">
                    <QRCodeSVG
                      id={`qr-${token.id}`}
                      value={passValue}
                      size={150}
                      level="H"
                      includeMargin={true}
                    />
                  </div>

                  {/* Token Code (for manual entry) */}
                  <div className="bg-gray-100 p-3 rounded-lg space-y-2">
                    <Label className="text-xs text-muted-foreground">Manual Entry Code</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono bg-white px-2 py-1 rounded border truncate">
                        {passValue}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(passValue);
                          toast({
                            title: 'Copied',
                            description: 'Token copied to clipboard',
                          });
                        }}
                        className="h-8 w-8 p-0"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge className={`${typeBadge} text-white text-xs`}>
                        {token.person_type}
                      </Badge>
                      {token.person_company && (
                        <span className="text-muted-foreground">{token.person_company}</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span className="truncate">{token.person_email}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {new Date(token.valid_from).toLocaleDateString()} - {new Date(token.valid_until).toLocaleDateString()}
                      </span>
                    </div>
                    {token.projects?.project_name && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <FolderKanban className="h-4 w-4" />
                        <span className="truncate">{token.projects.project_name}</span>
                      </div>
                    )}                    {token.depots?.name && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span className="truncate">{token.depots.name}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrintQR(token)}
                      className="flex items-center gap-2"
                    >
                      <Printer className="h-3 w-3" />
                      Print
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadQR(token)}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-3 w-3" />
                      Download
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopyLink(token)}
                      className="flex items-center gap-2"
                    >
                      <Copy className="h-3 w-3" />
                      Copy Link
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEmailQR(token)}
                      className="flex items-center gap-2"
                    >
                      <Mail className="h-3 w-3" />
                      Email
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects" className="space-y-4 mt-6">
          {selectedProject ? (
            // ── Project drill-down: show QR codes for the selected project ──
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedProject(null)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Projects
                </Button>
                <div>
                  <h2 className="text-xl font-semibold flex items-center gap-2">
                    <FolderKanban className="h-5 w-5 text-primary" />
                    {selectedProject.name}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {selectedProject.passes.length} QR code{selectedProject.passes.length !== 1 ? 's' : ''} linked to this project
                  </p>
                </div>
              </div>

              {selectedProject.passes.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-12">
                      <QrCode className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold">No QR codes for this project</h3>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedProject.passes.map((token) => {
                    const status = getStatusBadge(token);
                    const typeBadge = getPersonTypeBadge(token.person_type);
                    const passValue = token.pass_token || token.pass_code;
                    return (
                      <Card key={token.id} className="hover:shadow-lg transition-shadow">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">{token.person_name}</CardTitle>
                            <Badge className={status.variant}>{status.text}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex justify-center bg-white p-4 rounded-lg">
                            <QRCodeSVG
                              id={`proj-qr-${token.id}`}
                              value={passValue}
                              size={150}
                              level="H"
                              includeMargin={true}
                            />
                          </div>
                          <div className="bg-gray-100 p-3 rounded-lg space-y-2">
                            <Label className="text-xs text-muted-foreground">Manual Entry Code</Label>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 text-sm font-mono bg-white px-2 py-1 rounded border truncate">
                                {passValue}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  navigator.clipboard.writeText(passValue);
                                  toast({ title: 'Copied', description: 'Token copied to clipboard' });
                                }}
                                className="h-8 w-8 p-0"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <Badge className={`${typeBadge} text-white text-xs`}>{token.person_type}</Badge>
                              {token.person_company && (
                                <span className="text-muted-foreground">{token.person_company}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="h-4 w-4" />
                              <span className="truncate">{token.person_email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              <span>
                                {new Date(token.valid_from).toLocaleDateString()} -{' '}
                                {new Date(token.valid_until).toLocaleDateString()}
                              </span>
                            </div>
                            {token.depots?.name && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <MapPin className="h-4 w-4" />
                                <span className="truncate">{token.depots.name}</span>
                              </div>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Button variant="outline" size="sm" onClick={() => handlePrintQR(token)} className="flex items-center gap-2">
                              <Printer className="h-3 w-3" /> Print
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDownloadQR(token)} className="flex items-center gap-2">
                              <Download className="h-3 w-3" /> Download
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleCopyLink(token)} className="flex items-center gap-2">
                              <Copy className="h-3 w-3" /> Copy Link
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleEmailQR(token)} className="flex items-center gap-2">
                              <Mail className="h-3 w-3" /> Email
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            // ── Project list view ──
            (() => {
              const projectGroups = passes.reduce((acc, pass) => {
                if (pass.project_id) {
                  const key = pass.project_id;
                  if (!acc[key]) {
                    acc[key] = {
                      id: key,
                      name: pass.projects?.project_name || 'Unknown Project',
                      passes: [],
                    };
                  }
                  acc[key].passes.push(pass);
                }
                return acc;
              }, {});
              const projectList = Object.values(projectGroups);

              return projectList.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-12">
                      <FolderKanban className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold">No projects with QR codes</h3>
                      <p className="text-muted-foreground mt-2">
                        Approve project inductions to generate passes linked to projects
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {projectList.map((project) => {
                    const activeCount = project.passes.filter((p) => {
                      const now = new Date();
                      return !p.revoked_at && new Date(p.valid_from) <= now && new Date(p.valid_until) >= now;
                    }).length;
                    return (
                      <Card
                        key={project.id}
                        className="hover:shadow-lg transition-shadow cursor-pointer hover:border-primary"
                        onClick={() => setSelectedProject(project)}
                      >
                        <CardContent className="pt-6 pb-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                                <FolderKanban className="h-5 w-5 text-primary" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-foreground truncate">{project.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {project.passes.length} QR code{project.passes.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 mt-1" />
                          </div>
                          <div className="mt-4 flex gap-2">
                            <Badge className="bg-green-500 text-white text-xs">
                              {activeCount} active
                            </Badge>
                            {project.passes.length - activeCount > 0 && (
                              <Badge variant="outline" className="text-xs">
                                {project.passes.length - activeCount} other
                              </Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              );
            })()
          )}
        </TabsContent>

        {/* Bulk Creation Tab */}
        <TabsContent value="bulk" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Bulk QR Code Creation
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Select multiple approved passes and generate QR codes in bulk with audit trail
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Confirmation Checkbox */}
              <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium text-yellow-800">
                    Bulk generation confirmation required
                  </p>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="bulk-confirm"
                      checked={bulkConfirmation}
                      onCheckedChange={setBulkConfirmation}
                    />
                    <Label htmlFor="bulk-confirm" className="text-sm text-yellow-700 cursor-pointer">
                      I understand this will generate QR codes for {selectedPasses.length} selected pass{selectedPasses.length !== 1 ? 'es' : ''} and create an audit log entry
                    </Label>
                  </div>
                </div>
              </div>

              {/* Pass Selection List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">
                    Select Passes ({selectedPasses.length} selected)
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedPasses(filteredPasses.map(p => p.id))}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedPasses([])}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>

                {filteredPasses.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No approved passes available for bulk generation
                  </div>
                ) : (
                  <div className="grid gap-2 max-h-96 overflow-y-auto border rounded-lg p-4">
                    {filteredPasses.map((pass) => {
                      const status = getStatusBadge(pass);
                      const isSelected = selectedPasses.includes(pass.id);
                      
                      return (
                        <div
                          key={pass.id}
                          className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                            isSelected ? 'bg-primary/10 border-primary' : 'bg-background hover:bg-muted/50'
                          }`}
                          onClick={() => togglePassSelection(pass.id)}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => togglePassSelection(pass.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate">{pass.person_name}</p>
                              <Badge className={status.variant} variant="outline">
                                {status.text}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                              <span className="truncate">{pass.person_email}</span>
                              {pass.person_company && (
                                <span className="truncate">{pass.person_company}</span>
                              )}
                            </div>
                          </div>
                          <QrCode className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Generate Button */}
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  {selectedPasses.length > 0 && (
                    <span>Ready to generate {selectedPasses.length} QR code{selectedPasses.length !== 1 ? 's' : ''}</span>
                  )}
                </p>
                <Button
                  onClick={handleBulkGenerate}
                  disabled={selectedPasses.length === 0 || !bulkConfirmation || processingBulk}
                  className="flex items-center gap-2"
                >
                  {processingBulk ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Users className="h-4 w-4" />
                      Generate {selectedPasses.length} QR Code{selectedPasses.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bulk Import Tab */}
        <TabsContent value="import" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Bulk Pass Import (CSV)
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Import multiple passes at once by uploading a CSV file. Perfect for onboarding 300+ workers.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 1: Download Template */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">1</span>
                  Download CSV Template
                </h3>
                <p className="text-sm text-muted-foreground">
                  Download the template, fill in your worker details, then upload it below.
                </p>
                <Button onClick={handleDownloadTemplate} variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Download Template CSV
                </Button>
              </div>

              {/* Step 2: Upload CSV */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">2</span>
                  Upload Completed CSV
                </h3>
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer space-y-2">
                    <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {csvFile ? csvFile.name : 'Click to upload CSV file'}
                      </p>
                      <p className="text-xs text-muted-foreground">CSV files only</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Validation Errors */}
              {importErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-red-800">Validation Errors</h4>
                      <ul className="mt-2 space-y-1 text-xs text-red-700">
                        {importErrors.slice(0, 10).map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                        {importErrors.length > 10 && (
                          <li className="italic">... and {importErrors.length - 10} more errors</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview Table */}
              {parsedData.length > 0 && importErrors.length === 0 && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">3</span>
                    Preview & Confirm ({parsedData.length} records)
                  </h3>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-medium">Name</th>
                            <th className="text-left p-2 font-medium">Email</th>
                            <th className="text-left p-2 font-medium">Company</th>
                            <th className="text-left p-2 font-medium">Type</th>
                            <th className="text-left p-2 font-medium">Valid From</th>
                            <th className="text-left p-2 font-medium">Valid Until</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedData.slice(0, 50).map((row, index) => (
                            <tr key={index} className="border-t hover:bg-muted/50">
                              <td className="p-2">{row.person_name}</td>
                              <td className="p-2 truncate max-w-[200px]">{row.person_email}</td>
                              <td className="p-2">{row.person_company || '-'}</td>
                              <td className="p-2">
                                <Badge variant="outline" className="text-xs">
                                  {row.person_type}
                                </Badge>
                              </td>
                              <td className="p-2">{row.valid_from}</td>
                              <td className="p-2">{row.valid_until}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {parsedData.length > 50 && (
                        <div className="p-2 text-xs text-muted-foreground text-center bg-muted">
                          Showing first 50 of {parsedData.length} records
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Confirmation Checkbox */}
                  <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg mt-4">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div className="flex-1 space-y-2">
                      <p className="text-sm font-medium text-yellow-800">
                        Import confirmation required
                      </p>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="import-confirm"
                          checked={importConfirmation}
                          onCheckedChange={setImportConfirmation}
                        />
                        <Label htmlFor="import-confirm" className="text-sm text-yellow-700 cursor-pointer">
                          I confirm this will create {parsedData.length} pass{parsedData.length !== 1 ? 'es' : ''} with QR codes and log to audit trail
                        </Label>
                      </div>
                    </div>
                  </div>

                  {/* Import Button */}
                  <div className="flex items-center justify-between pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCsvFile(null);
                        setParsedData([]);
                        setImportErrors([]);
                        setImportConfirmation(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleBulkImport}
                      disabled={!importConfirmation || processingImport}
                      className="flex items-center gap-2"
                    >
                      {processingImport ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4" />
                          Import {parsedData.length} Pass{parsedData.length !== 1 ? 'es' : ''}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Instructions */}
              {!csvFile && (
                <div className="bg-muted/50 border border-border rounded-lg p-4">
                  <h4 className="text-sm font-semibold mb-2">CSV Format Requirements:</h4>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• <strong>Name</strong> - Full name (required)</li>
                    <li>• <strong>Email</strong> - Valid email address (required)</li>
                    <li>• <strong>Company</strong> - Company name (optional)</li>
                    <li>• <strong>Person Type</strong> - Must be: contractor, visitor, or employee (required)</li>
                    <li>• <strong>Valid From</strong> - Date in YYYY-MM-DD format (required)</li>
                    <li>• <strong>Valid Until</strong> - Date in YYYY-MM-DD format (required)</li>
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Generate QR Modal */}
      <Dialog open={isGenerateModalOpen} onOpenChange={setIsGenerateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate QR Code</DialogTitle>
            <DialogDescription>
              Create a new QR code for visitor check-in
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="person_name">Full Name *</Label>
              <Input
                id="person_name"
                value={formData.person_name}
                onChange={(e) => setFormData({ ...formData, person_name: e.target.value })}
                placeholder="John Smith"
              />
            </div>

            <div>
              <Label htmlFor="person_email">Email *</Label>
              <Input
                id="person_email"
                type="email"
                value={formData.person_email}
                onChange={(e) => setFormData({ ...formData, person_email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>

            <div>
              <Label htmlFor="person_company">Company</Label>
              <Input
                id="person_company"
                value={formData.person_company}
                onChange={(e) => setFormData({ ...formData, person_company: e.target.value })}
                placeholder="ABC Construction"
              />
            </div>

            <div>
              <Label htmlFor="person_type">Person Type *</Label>
              <Select
                value={formData.person_type}
                onValueChange={(value) => setFormData({ ...formData, person_type: value })}
              >
                <SelectTrigger id="person_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="visitor">Visitor</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="valid_from">Valid From *</Label>
                <Input
                  id="valid_from"
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="valid_until">Valid Until *</Label>
                <Input
                  id="valid_until"
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsGenerateModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerateQR}
              disabled={!formData.person_name || !formData.person_email || !formData.valid_until}
            >
              Generate QR Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Modal */}
      {selectedToken && (
        <div className="print-only fixed inset-0 bg-white p-8 z-50" style={{ display: isPrintModalOpen ? 'block' : 'none' }}>
          <div className="text-center space-y-6">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: '#E5B800' }}>Depot Check-In</h1>
              <p className="text-lg text-gray-600 mt-2">Visitor QR Code</p>
            </div>

            <div className="bg-white p-8 inline-block rounded-lg border-4 border-gray-200">
              <QRCodeSVG
                value={selectedToken.pass_token || selectedToken.pass_code}
                size={300}
                level="H"
                includeMargin={true}
              />
            </div>

            <div className="space-y-2">
              <p className="text-2xl font-bold">{selectedToken.person_name}</p>
              {selectedToken.person_company && (
                <p className="text-xl text-gray-600">{selectedToken.person_company}</p>
              )}
              <p className="text-gray-500">{selectedToken.person_email}</p>
            </div>

            <div className="border-t pt-4 space-y-1 text-sm text-gray-500">
              <p>Valid from: {new Date(selectedToken.valid_from).toLocaleDateString()}</p>
              <p>Valid until: {new Date(selectedToken.valid_until).toLocaleDateString()}</p>
            </div>

            <div className="text-xs text-gray-400 mt-8">
              <p>Scan this QR code at the kiosk to check in</p>
              <p className="mt-1">Token: {selectedToken.pass_token || selectedToken.pass_code}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodeGenerator;
